import React from "react";
import {useManualFootageReview} from "./useManualFootageReview";
import ContentTemplateLibrary from "../content/ContentTemplateLibrary";
import { Alert, Button, Stack, Typography } from "@mui/material";
import SocialsPage from "./SocialsPage";
import { socialsDestination } from "../../lib/socialsDeepLink";
import { contentReviewService, ContentReviewQueue, ContentWorkerHealth, ContentPostedSummary, ContentReviewItem, ContentMarkPostedInput } from "../../services/contentReviewService";
import { redditAnswerService, RedditQueue } from "../../services/redditAnswerService";
import { useSocialsDecisions } from "./useSocialsDecisions";
import ContentMarkPostedDialog from "../content/ContentMarkPostedDialog";
import { beatRepetition } from "../content/sceneBeats";
import type { HealthRow } from "./BehindTheScenesDialog";
import type { EngageAction } from "./engage/EngageTab";

const EMPTY_ITEMS:ContentReviewItem[]=[];
export default function SocialsConnectedPage({onTurnIntoLead,userId}: {userId?:string;onTurnIntoLead?: (source: string) => void}) {
  const [destination] = React.useState(() => socialsDestination(window.location.search));
  const [activeTab, setActiveTab] = React.useState(destination.tab);
  const [refreshVersion, setRefreshVersion] = React.useState(0);
  const [healthOpened, setHealthOpened] = React.useState(false);
  const [queue, setQueue] = React.useState<ContentReviewQueue | null>(null);
  const [engage, setEngage] = React.useState<RedditQueue | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [engageLoading, setEngageLoading] = React.useState(true);
  const [engageError, setEngageError] = React.useState<string | null>(null);
  const [engageBusy, setEngageBusy] = React.useState<string | null>(null);
  const engageLock = React.useRef(false);
  const generation = React.useRef(0);
  const decisions = useSocialsDecisions();
  const [health, setHealth] = React.useState<ContentWorkerHealth | null>(null);
  const [summary, setSummary] = React.useState<ContentPostedSummary | null>(null);
  const [posting, setPosting] = React.useState<ContentReviewItem | null>(null);
  const [postBusy, setPostBusy] = React.useState(false);
  const postLock = React.useRef(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const load = React.useCallback(async () => {
    const request = ++generation.current;
    setLoading(true); setError(null);
    await Promise.all([
      contentReviewService.postedSummary().then(value => { if (request === generation.current) setSummary(value); })
        .catch(() => { if (request === generation.current) setSummary({status:"unknown",value:null,definition_id:"content.posted.v1",computed_at:"",last_posted_at:null,per_platform:null}); }),
      Promise.all([contentReviewService.list("review", 60), contentReviewService.list("approved", 60)])
        .then(pages => { if (request === generation.current) {
          const byId = new Map(pages.flatMap(page => page.items).map(item => [item.id, item]));
          setQueue({...pages[0], items:[...byId.values()], counts_complete:pages.every(page => page.counts_complete)});
        } })
        .catch(() => { if (request === generation.current) { setQueue(null); setError("Could not read the review queue. Try again."); } })
        .finally(() => { if (request === generation.current) setLoading(false); }),
    ]);
  }, []);
  React.useEffect(() => { void load(); return () => { generation.current++; }; }, [load]);
  // Secondary desks are read only after the operator opens them. Unknown
  // counts stay unknown on Overview instead of fetching hidden work for badges.
  React.useEffect(() => {
    if (activeTab !== "engage") return;
    let current = true;
    setEngageLoading(true); setEngageError(null);
    redditAnswerService.list("all")
      .then(value => { if (current) setEngage(value); })
      .catch(() => { if (current) setEngageError("Could not read conversations. Try again."); })
      .finally(() => { if (current) setEngageLoading(false); });
    return () => { current = false; };
  }, [activeTab, refreshVersion]);
  React.useEffect(() => {
    if (!healthOpened) return;
    let current = true;
    contentReviewService.health().then(value => { if (current) setHealth(value); })
      .catch(() => { if (current) setHealth(null); });
    return () => { current = false; };
  }, [healthOpened, refreshVersion]);
  React.useEffect(() => {
    if (activeTab !== "reels" || loading) return;
    let current = true;
    contentReviewService.list("rejected", 60).then(page => {
      if (current) setQueue(previous => previous ? {...previous,
        items: [...new Map([...previous.items, ...page.items].map(item => [item.id, item])).values()]} : previous);
    }).catch(() => { if (current) setError("Could not read rejected reviews. Try again."); });
    return () => { current = false; };
  }, [activeTab, loading]);
  React.useEffect(() => { if (queue) decisions.reconcile(queue.items); }, [queue, decisions.reconcile]);
  const [revisionQueued, setRevisionQueued] = React.useState(false);
  const needsSync = revisionQueued || Object.values(decisions.states).some(state => state.kind === "busy" || state.kind === "unknown") || Boolean(queue?.items.some(item =>
    item.command && (["queued", "leased", "retry"].includes(item.command.state) ||
      (item.command.verdict.startsWith("K-") && item.command.state === "applied" && Boolean(item.command.child_package_id) && !item.command.child_ready))));
  React.useEffect(() => {
    if (!needsSync) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refreshReviews = async () => {
      const request = generation.current;
      if (document.visibilityState !== "hidden") {
        try {
          const pages = await Promise.all([contentReviewService.list("review",60),contentReviewService.list("rejected",60),contentReviewService.list("approved",60)]);
          if (!cancelled && request === generation.current) {
            const byId = new Map(pages.flatMap(page => page.items).map(item => [item.id,item]));
            setQueue({...pages[0],items:[...byId.values()],counts_complete:pages.every(page => page.counts_complete)});
            setRevisionQueued(false); setNotice(current => current === "Could not refresh review outcomes. Showing the last saved state." ? null : current);
          }
        } catch {
          // Retain the last readable queue and its approval guards on a failed read.
          if (!cancelled) setNotice("Could not refresh review outcomes. Showing the last saved state.");
        }
      }
      if (!cancelled) timer = setTimeout(refreshReviews, 10000);
    };
    timer = setTimeout(refreshReviews, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [needsSync]);
  const actOnEngage = async (action: EngageAction) => {
    if (engageLock.current) return;
    engageLock.current = true; setEngageBusy(action.leadId); setEngageError(null);
    try {
      await redditAnswerService.action(action.leadId, {action:action.action, final_text:action.finalText,
        suggested_text:action.suggestedText, dismiss_reason:action.dismissReason});
      if (action.action === "copied") {
        await navigator.clipboard.writeText(action.finalText);
      }
      const value = await redditAnswerService.list("all"); setEngage(value);
    } catch { setEngageError("Could not confirm this action. Refresh conversations before trying again."); }
    finally { engageLock.current = false; setEngageBusy(null); }
  };
  const recordPost = async (item: ContentReviewItem, input: ContentMarkPostedInput) => {
    if (postLock.current || footage.blocked(item)) return;
    postLock.current = true; setPostBusy(true);
    try {
      const record = await contentReviewService.markPosted(item.id, input);
      setNotice(record.replayed ? "Already recorded — nothing changed" : "Post recorded");
      setPosting(null); await load();
    } catch { setNotice("Couldn’t record that post — try again"); }
    finally { postLock.current = false; setPostBusy(false); }
  };
  const [templates,setTemplates]=React.useState(false);
  const footage=useManualFootageReview(userId,queue?.items??EMPTY_ITEMS,()=>{setRevisionQueued(true);void load();});
  const postedHeadline = summary === null ? "Posted — reading ledger…"
    : summary.status !== "ok" || summary.value === null ? "Posted — unknown (ledger unreadable)"
    : summary.value === 0 ? "No posts recorded yet"
    : `Posted ${summary.value} · last ${summary.last_posted_at ? new Date(summary.last_posted_at).toLocaleDateString(undefined,{month:"short",day:"numeric"}) : "unknown"}`;
  const repetitions = queue ? beatRepetition(queue.items.map(item => item.scenes)) : null;
  const rows: HealthRow[] = [
    {label:"Engine last ran", value:health?.last_heartbeat_at ? new Date(health.last_heartbeat_at).toLocaleString() : "Not known", tone:"watch"},
    {label:"Review sync", value:loading ? "Reading…" : error ? "Could not read" : `${queue?.items.length ?? "—"} reels read`, tone:"watch"},
    {label:"Decisions that failed to apply", value:health ? `${health.exhausted_commands} reported (all time)` : "Not known", tone:"watch", href:"?page=content&view=legacy"},
    {label:"Token expiry",value:"Not available from this connection",tone:"watch"},
    {label:"Wording variety", value:repetitions === null ? "Not read" : repetitions.length === 0 ? "No scene captions to compare" : repetitions.map(row =>
      `${row.beat_role}: ${row.distinct}/${row.total} distinct${row.mostRepeated ? ` · “${row.mostRepeated.caption}” × ${row.mostRepeated.count}` : ""}`).join("; "),tone:"watch"},
  ];
  return <Stack spacing={1}>
    {notice ? <Alert severity="info">{notice}</Alert> : null}
    <ContentMarkPostedDialog item={posting} busy={postBusy} onCancel={() => setPosting(null)}
      onConfirm={(item,input) => void recordPost(item,input)} onDownload={item => { void contentReviewService.download(item).catch(() => setNotice("Couldn’t save that reel — try again")); }} />
    {templates?<ContentTemplateLibrary/>:null}
    <SocialsPage renderFootage={footage.render} footageBlocked={footage.blocked} connectedSchedule items={queue?.items ?? null} totalKnown={queue?.counts_complete && typeof queue.counts.all === "number" ? queue.counts.all : null}
      headerMeta={<Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" data-testid="socials-posted-summary">{postedHeadline}</Typography>
        <Button onClick={()=>setTemplates(v=>!v)}>Templates</Button>
        <Button href="?page=content&view=legacy" size="small">Open previous Content page</Button>
      </Stack>}
      loading={loading} error={error} onRefresh={() => { setRefreshVersion(v => v + 1); void load(); }} onRevisionQueued={() => setRevisionQueued(true)}
      health={rows} onMarkPosted={setPosting}
      scheduleBlockedReason="Schedule approved reels from the Overview calendar."
      initialTab={destination.tab} initialLead={destination.lead} onTabChange={setActiveTab} onHealthOpen={() => setHealthOpened(true)}
      engageLeads={engage?.items} engageCounts={engage?.counts}
      engageLoading={engageLoading} engageError={engageError} engageBusyId={engageBusy}
      onTurnIntoLead={onTurnIntoLead ? lead => onTurnIntoLead(lead.source_url) : undefined}
      onEngageAction={action => void actOnEngage(action)}
      decisionStates={decisions.states} onRetryDecision={item => { if(footage.blocked(item))return; void decisions.retry(item).then(response => { setRevisionQueued(true); }); }}
      onVerdict={(item,verdict,note) => { if(footage.blocked(item))return; void decisions.decide(item,verdict,note).then(response => {
        setRevisionQueued(true);
      }); }} />
  </Stack>;
}
